/** The handful of line icons the launcher uses (inline SVG, no icon pack). */
export { LogoMark } from '../lib/LogoMark';

type P = { size?: number; className?: string };

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export function BellIcon({ size = 24, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M6 9a6 6 0 1 1 12 0c0 4.5 1.6 6.3 2.4 7H3.6C4.4 15.3 6 13.5 6 9Z" />
      <path d="M10 19.5a2.2 2.2 0 0 0 4 0" />
    </svg>
  );
}

export function GearIcon({ size = 24, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.6 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.6-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

export function ChevronLeftIcon({ size = 24, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 20, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

/** iOS share glyph (box with an up arrow). */
export function ShareIcon({ size = 24, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M8 11H6.5A1.5 1.5 0 0 0 5 12.5v7A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5H16" />
    </svg>
  );
}

/** iOS "Add to Home Screen" glyph (rounded square with a plus). */
export function AddSquareIcon({ size = 24, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}

/** A QR viewfinder: four corners around a dot grid. */
export function ScanIcon({ size = 22, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" />
      <path d="M9 9h2v2H9zM13 13h2v2h-2zM13 9h2M9 15h2" />
    </svg>
  );
}

/** A wrench: the way into the builder from the launcher. */
export function WrenchIcon({ size = 18, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M14.6 5.2a4.3 4.3 0 0 0-5.3 5.6L4 16.1a1.6 1.6 0 0 0 0 2.3l1.6 1.6a1.6 1.6 0 0 0 2.3 0l5.3-5.3a4.3 4.3 0 0 0 5.6-5.3l-2.7 2.7-2.4-.6-.6-2.4 2.7-2.7Z" />
    </svg>
  );
}

export function EyeIcon({ size = 18, className }: P) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="2.8" />
    </svg>
  );
}
